<template>
	<page-container :page="page" :class="page.headingImage ? '' : 'no-slider'">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		>
			<template #heroContent>
				<div v-if="defaults[locale].homepage.headingTitle" class="hero-content">
					<span class="hero-title">
						{{ defaults[locale].homepage.headingTitle }}
					</span>
					<span class="hero-subtitle">
						{{ defaults[locale].homepage.headingSubTitle }}
					</span>
				</div>

				<booking-form :booking-form="defaults[locale].homepage" />

				<div class="slider-overlay" />
			</template>
		</heading-image>

		<main-content
			:title="page.title || page.header"
			:subtitle="page.subtitle"
			:content="page.content"
			color="white"
		>
			<template #introGraphic>
				<div class="intro-graphic" />
			</template>
			<template #backgroundPattern>
				<div class="background-pattern" />
			</template>
		</main-content>

		<booking-benefits
			:title="defaults[locale].homepage.sectionBenefitsTitle"
			:subtitle="defaults[locale].homepage.sectionBenefitsSubtitle"
			:benefits="defaults[locale].homepage.bookingBenefits"
			:button-text="defaults[locale].website.book"
			:image="{
				image: defaults[locale].homepage.sectionBenefitsImage,
				imageAlt: defaults[locale].homepage.sectionBenefitsImageAlt,
				imageWebp: defaults[locale].homepage.sectionBenefitsImageWebp,
			}"
		/>

		<!-- <link-blocks :items="promoblocks" /> -->

		<contentblocks v-if="page?.contentblocks && page.contentblocks.length" :contentblocks="page.contentblocks" />

		<slider-gallery class="fade-animate up" :items="gallery.filter((item) => item?.showOnIndex)" />

		<hotel-facilities
			:header="defaults[locale].homepage.sectionHotelFacilitiesTitle"
			:items="defaults[locale].homepage.hotelFacilities"
		/>

		<highlights-slider
			:items="locationBlocks"
			:header="defaults[locale].homepage.sectionHighlightsSliderTitle"
			:content="defaults[locale].homepage.sectionHighlightsSliderContent"
		/>
		<highlights-promo-map
			:header="defaults[locale].homepage.howToGetThereTitle"
			:items="
				highlights
					.map((category) => category?.items)
					.flat()
					.filter((item) => item?.showOnIndex)
			"
		/>
		<frequently-asked-questions
			v-if="page && page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:subtitle="page.frequentlyAskedQuestion.subtitle"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();

await fetchPage();
await fetchDefaults();

/*
const { data: promoblocks } = await useWebsiteFetch('promoblocks', {
	query: { language: locale.value },
	key: `${locale.value}/promoblocks`,
});
*/

const { data: gallery } = await useWebsiteFetch('gallery', {
	query: { language: locale.value },
	key: `${locale.value}/gallery`,
});

const { data: highlights } = await useWebsiteFetch('highlights', {
	query: { language: locale.value },
	key: `${locale.value}/highlights`,
});

const { data: locationBlocks } = await useWebsiteFetch('locationBlocks', {
	query: { language: locale.value },
	key: `${locale.value}/locationBlocks`,
});
</script>

<style lang="scss" scoped>
.hero-content {
	position: absolute;
	top: calc(50% - 120px);
	left: 0;
	right: 0;
	z-index: 3;
	text-align: center;
	padding: 0 20px;
	color: #fff;
	margin: 0 auto;
	max-width: 890px;
	animation: fade-from-bottom 1.4s ease;

	span {
		color: #fff;
		font-weight: 400;
		font-size: 60px;
		line-height: 64px;
		width: 100%;
		font-family: var(--heading-font-family);
		display: inline-block;
		margin: 0 0 10px;
	}

	.hero-subtitle {
		font-weight: 700;
		font-family: var(--body-font-family);
		text-transform: uppercase;
		font-size: 20px;
		letter-spacing: 2px;
	}
}

.background-pattern {
	background: url('~/assets/images/arq-bg-pattern-repeatable.png') repeat-x top center var(--primary-background-color);
	position: absolute;
	inset: 0;
	z-index: 1;

	&::before {
		content: '';
		display: block;
		z-index: 1;
		position: absolute;
		inset: 0;
		background: linear-gradient(180deg, rgb(70 79 44 / 40%) 0%, #464f2c 90%);
	}
}

.slider-overlay {
	position: absolute;
	inset: auto 0 0;
	height: 450px;
	background: linear-gradient(0deg, #000 0%, rgb(102 102 102 / 0%) 100%);
	z-index: 1;
}

.intro-graphic {
	background: url('~/assets/images/arq-logo-icon.svg') no-repeat center center;
	background-size: 34px;
	width: 34px;
	height: 42px;
	margin: 0 auto 30px;
}

:deep(.intro),
.intro {
	color: #fff;
	padding: 100px 0;
}

@media (max-width: 1060px) {
	:deep(.intro),
	.intro {
		color: #fff;
		padding: 60px 0;
	}
}

@media (max-width: 880px) {
	.hero-content {
		.hero-title {
			font-size: 52px;
			line-height: 58px;
		}

		.hero-subtitle {
			font-size: 18px;
			line-height: 22px;
		}
	}
}

@media (max-width: 680px) {
	.hero-content {
		.hero-title {
			font-size: 42px;
			line-height: 46px;
		}

		.hero-subtitle {
			font-size: 15px;
		}
	}
}
</style>
